'use client'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import type { FocusEventHandler, ReactNode } from 'react'

import { FieldCaption } from '@src/components/FieldCaption/FieldCaption'
import { cn } from '@src/utils/tailwind'

export interface DropdownItem {
  id: string
  name: string | ReactNode
  elementLeft?: ReactNode
}
export interface Props {
  items: DropdownItem[]
  selectedId?: string
  onItemIdSelected: (id?: string) => void
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLDivElement>
  placeholder?: string
  listboxOptionsClassNames?: string
  containerClassNames?: string
  size?: 'small' | 'large'
  isInvalid?: boolean
  captionText?: string
  disabled?: boolean
  showElementLeftInOptions?: boolean
  listboxOptionsItemClassNames?: string
  'data-testid'?: string
}

export const Dropdown = (props: Props) => {
  const {
    items,
    onItemIdSelected,
    onBlur,
    selectedId,
    placeholder,
    listboxOptionsClassNames,
    listboxOptionsItemClassNames,
    size = 'small',
    containerClassNames,
    isInvalid,
    captionText,
    disabled,
    showElementLeftInOptions = false,
    'data-testid': dataTestId,
  } = props
  const itemForId = items.find((item) => item.id === selectedId)
  const selected = placeholder && !itemForId ? undefined : (itemForId ?? items?.[0])

  return (
    <div className="bg-white text-black">
      <Listbox
        value={selected ?? { id: '', name: '' }}
        disabled={disabled}
        onChange={(e) => {
          onItemIdSelected(e.id)
        }}
      >
        <ListboxButton
          data-testid={dataTestId}
          onBlur={onBlur}
          className={cn(
            'relative flex w-full items-center justify-between rounded-[4px] border border-bodi-gray-butters p-4 text-left text-body-base text-bodi-gray-dark',
            'focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-bodi-gray-butters/25',
            containerClassNames,
            {
              'text-bodi-gray-medium': !selected?.name && placeholder,
              'min-h-[45px]': size === 'small',
              'min-h-[56px]': size === 'large',
              'border-bodi-red-deep-500': isInvalid,
              'bg-bodi-gray-pearl text-bodi-gray-disabled': disabled,
            }
          )}
        >
          <div className="flex items-start gap-3">
            <div>{selected?.elementLeft}</div>
            <div>{selected?.name ?? placeholder}</div>
          </div>
          <ChevronDownIcon className="pointer-events-none size-4" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          onBlur={onBlur}
          transition
          className={cn(
            'mt-1 w-(--button-width) rounded-[4px] border border-bodi-gray-butters bg-white py-1.5 text-body-base shadow-lg [--anchor-gap:var(--spacing-1)] focus:outline-none',
            'transition duration-100 ease-in data-closed:opacity-0',
            listboxOptionsClassNames
          )}
        >
          {items.map((item) => (
            <ListboxOption
              key={item.id}
              value={item}
              className={cn(
                'group flex cursor-default items-start gap-2 px-3 py-1.5 text-bodi-black select-none data-focus:bg-bodi-green-bright-300',
                listboxOptionsItemClassNames
              )}
            >
              <CheckIcon className="invisible mt-1 size-4 text-bodi-green-deep-500 group-data-selected:visible" />
              {showElementLeftInOptions && <div>{item.elementLeft}</div>}
              <div className="text-body-base">{item.name}</div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
      {captionText && (
        <FieldCaption captionText={captionText} isInvalid={isInvalid} disabled={disabled} />
      )}
    </div>
  )
}
